/**
 * Public model catalogue + pricing, fetched from the router origin
 * (`/v1/models`, `/api/models/capabilities`, `/api/pricing` — all public, no
 * session). Shared by the landing page and the public models view.
 *
 * Resilient by design: each source resolves independently (Promise.allSettled),
 * so a slow chain RPC for capabilities never blanks the price teaser.
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  fetchCapabilities,
  fetchModels,
  fetchPricing,
  type CapabilityEntry,
  type ModelEntry,
  type PricingResponse,
} from '../api/docs';

export const useCatalogStore = defineStore('catalog', () => {
  const models = ref<ModelEntry[]>([]);
  const capabilities = ref<CapabilityEntry[]>([]);
  const pricing = ref<PricingResponse | null>(null);
  const loading = ref(false);
  const loaded = ref(false);

  /** Lowest non-zero per-1M USD price across all models, or null. */
  const minPer1mUsd = computed<number | null>(() => {
    const rows = pricing.value?.models ?? [];
    const vals = rows
      .map((r) => r.per_1m_usd)
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0);
    return vals.length > 0 ? Math.min(...vals) : null;
  });

  const modelCount = computed<number>(() => {
    const ids = new Set<string>();
    for (const m of models.value) ids.add(m.id);
    for (const c of capabilities.value) ids.add(c.id);
    // Pricing is the fastest public endpoint and already carries the same
    // canonical model ids. Count it too so the hero does not show an empty
    // "models live" slot while /v1/models or capabilities are still loading.
    for (const p of pricing.value?.models ?? []) ids.add(p.model);
    return ids.size;
  });

  /** Largest advertised context window across models, or null. */
  const maxContext = computed<number | null>(() => {
    const wins = capabilities.value
      .map((c) => c.context_window)
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0);
    return wins.length > 0 ? Math.max(...wins) : null;
  });

  async function load(force = false): Promise<void> {
    if (loading.value) return;
    if (loaded.value && !force) return;
    loading.value = true;
    const results = await Promise.allSettled([fetchModels(), fetchCapabilities(), fetchPricing()]);
    const [m, c, p] = results;
    if (m.status === 'fulfilled') models.value = m.value;
    if (c.status === 'fulfilled') capabilities.value = c.value.models;
    if (p.status === 'fulfilled') pricing.value = p.value;
    loading.value = false;
    loaded.value = true;
  }

  return { models, capabilities, pricing, loading, loaded, minPer1mUsd, modelCount, maxContext, load };
});
